import { useEffect, useRef, useState } from "react";
import useMessage from "./useMessage";

type Props = {
  callback: () => Promise<any>;
  maxAttempts?: number;
  delay?: number;
  errorMessage?: string;
};

// TODO: rename this to usePolling after current usePolling.ts is removed
const useBackendPolling = ({
  callback,
  maxAttempts = 12,
  delay = 5000,
  errorMessage,
}: Props) => {
  const savedCallback = useRef<() => Promise<any>>();
  const intervalIdRef = useRef<NodeJS.Timeout>();
  const timeoutIdRef = useRef<NodeJS.Timeout>();
  const [isPolling, setIsPolling] = useState<boolean>(false);
  const [isTimeout, setIsTimeout] = useState<boolean>(false);
  const [attempts, setAttempts] = useState<number>(0);
  const [error, setError] = useState<Error | null>(null);

  const { sendMessage } = useMessage();

  const clearErrors = () => {
    setError(null);
    setIsTimeout(false);
  };

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  const startPolling = async () => {
    setIsPolling(true);
    setIsTimeout(false);

    intervalIdRef.current = setInterval(async () => {
      if (savedCallback.current) {
        try {
          await savedCallback.current();
          setAttempts((prevAttempts) => prevAttempts + 1);
        } catch (err) {
          if (err instanceof Error) {
            setError(err);
          }
        }
      }
    }, delay);
  };

  const stopPolling = () => {
    setIsPolling(false);
    clearTimeout(timeoutIdRef.current);
    clearInterval(intervalIdRef.current);
    setAttempts(0);
  };

  useEffect(() => {
    if (error) {
      // use setTimeout to ensure stopPolling is called in the next tick if an error occurs
      const errorId = setTimeout(() => {
        stopPolling();
        sendMessage(error.message, "error");
      }, 0);

      return () => {
        clearTimeout(errorId);
      };
    }
    // sendMessage as dependency creates infite loop
    // eslint-disable-next-line
  }, [error]);

  useEffect(() => {
    if (isPolling && attempts >= maxAttempts) {
      // use setTimeout to ensure stopPolling is called in the next tick, when max attempts are reached
      const timeoutId = setTimeout(() => {
        setIsTimeout(true);
        stopPolling();
        setAttempts(0);
        !!errorMessage && sendMessage(errorMessage, "error");
      }, 0);

      return () => {
        clearTimeout(timeoutId);
      };
    }
    // sendMessage as dependency creates infite loop
    // eslint-disable-next-line
  }, [attempts, maxAttempts, isPolling]);

  return {
    isPolling,
    isTimeout,
    startPolling,
    stopPolling,
    error,
    clearErrors,
  };
};

export default useBackendPolling;
